/**
 * @returns {Object} Admin domain methods (bound to `this` of the composed apiService)
 */
export function createAdminMethods() {
  return {
    /**
     * Merge encounters and related records from a source case into a target case.
     * @param {Object} params
     * @param {string} params.source_case_id
     * @param {string} params.target_case_id
     * @param {'all'|'selected'} params.mode
     * @param {string[]} [params.encounter_ids] - Required when mode is 'selected'
     * @param {string} [params.source_case_status] - Status to set source case to (default: 'cancelled')
     * @returns {Promise<{success: boolean, encounters_moved: number}>}
     */
    async mergeCases(params) {
      return this.request('/cases-merge', {
        method: 'POST',
        body: JSON.stringify(params),
      });
    },

    /**
     * Merge all records from a source patient into a target patient.
     * @param {Object} params
     * @param {string} params.source_patient_id
     * @param {string} params.target_patient_id
     * @param {boolean} [params.delete_source] - Whether to delete the source patient after merge
     * @returns {Promise<{success: boolean, tables_updated: number}>}
     */
    async mergePatients(params) {
      return this.request('/patients-merge', {
        method: 'POST',
        body: JSON.stringify(params),
      });
    },

    /**
     * Plans of care created on/after a date whose certification_end equals the
     * certification_end of the plan they replaced (admin probe, cross-org gated
     * on the backend to own org or Prism staff).
     *
     * Xano: GET /admin/poc-cert-end-carryover (admin_poc-cert-end-carryover_GET.xs, both envs)
     *
     * @param {Object} params
     * @param {string} params.organization_id
     * @param {string} params.created_after - YYYY-MM-DD (UTC midnight, inclusive)
     * @param {string} [params.created_before] - YYYY-MM-DD (UTC midnight, exclusive) to run in windows
     * @param {string} [params.note_field_id] - template field id whose stored note entry is returned per row
     * @param {number} [params.limit] - row cap, 1-2000 (default 500)
     * @returns {Promise<{organization_id: string, created_after: string, created_before: string, limit: number, note_field_id: string, items: Object[], total_count: number, scanned_count: number|null, capped: boolean, computed_at: number}>}
     */
    async getPocCertEndCarryover(params = {}) {
      const query = new URLSearchParams();
      if (params.organization_id) query.append('organization_id', params.organization_id);
      if (params.created_after) query.append('created_after', params.created_after);
      if (params.created_before) query.append('created_before', params.created_before);
      if (params.note_field_id) query.append('note_field_id', params.note_field_id);
      if (params.limit) query.append('limit', String(params.limit));
      return this.request(`/admin/poc-cert-end-carryover?${query.toString()}`, {
        method: 'GET',
      });
    },
  };
}
